import React from 'react';
import { ResultType } from '../../../../../store/Models/Api/ApiResponse';
import {
    DataCollectionGapSummaryMetadata,
    DataCollectionGapSummaryPageResponse
} from '../../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { ToxnotServiceClient } from '../../../../../store/ToxnotServiceClient';
import { useLocalizer } from '../../hooks/useLocalizer';

interface UseGapSummaryDataOptions {
    requestId: string;
    operationId: string;
    sourceGapSummary?: DataCollectionGapSummaryMetadata | null;
    onSummaryLoaded: (summary: DataCollectionGapSummaryPageResponse) => void;
}

interface UseGapSummaryDataResult {
    summary: DataCollectionGapSummaryPageResponse | null;
    isLoading: boolean;
    errorMessage: string | null;
    setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
    markGapReportSent: (recipientEmail: string) => Promise<void>;
}

export const useGapSummaryData = ({
    requestId,
    operationId,
    sourceGapSummary,
    onSummaryLoaded
}: UseGapSummaryDataOptions): UseGapSummaryDataResult => {
    const localize = useLocalizer();
    const serviceClient = React.useMemo(() => new ToxnotServiceClient(), []);
    const [summary, setSummary] = React.useState<DataCollectionGapSummaryPageResponse | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const isMountedRef = React.useRef(true);
    const summaryRefreshInFlightRef = React.useRef<{
        summaryKey: string;
        promise: Promise<void>;
    } | null>(null);
    const activeSummaryKey = `${requestId}:${operationId}`;
    const activeSummaryKeyRef = React.useRef(activeSummaryKey);
    activeSummaryKeyRef.current = activeSummaryKey;

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refreshSummary = React.useCallback(async (
        showLoading: boolean,
        showRefreshError: boolean
    ): Promise<void> => {
        const requestedSummaryKey = `${requestId}:${operationId}`;
        const inFlightRefresh = summaryRefreshInFlightRef.current;
        if (inFlightRefresh?.summaryKey === requestedSummaryKey) {
            await inFlightRefresh.promise;
            return;
        }

        if (!isMountedRef.current || activeSummaryKeyRef.current !== requestedSummaryKey) {
            return;
        }

        const refreshPromise = (async (): Promise<void> => {
            if (showLoading) {
                setIsLoading(true);
            }
            if (showRefreshError) {
                setErrorMessage(null);
            }

            if (!requestId || !operationId) {
                if (isMountedRef.current && activeSummaryKeyRef.current === requestedSummaryKey) {
                    setIsLoading(false);
                    if (showRefreshError) {
                        setErrorMessage(localize('Gap summary is not available for this survey request.'));
                    }
                }
                return;
            }

            const apiResult = await serviceClient.getDataCollectionGapSummary(requestId, operationId);

            if (!isMountedRef.current || activeSummaryKeyRef.current !== requestedSummaryKey) {
                return;
            }

            if (apiResult.success && apiResult.data) {
                setSummary(apiResult.data);
                setIsLoading(false);
                setErrorMessage(null);
                onSummaryLoaded(apiResult.data);
                return;
            }

            if (showLoading) {
                setIsLoading(false);
            }
            if (showRefreshError) {
                setErrorMessage(apiResult.result === ResultType.NotFound
                    ? localize('Gap summary is not available for this survey request.')
                    : localize('Sorry, there was a problem accessing the service. Please try again.'));
            }
        })();

        summaryRefreshInFlightRef.current = {
            summaryKey: requestedSummaryKey,
            promise: refreshPromise
        };

        try {
            await refreshPromise;
        }
        finally {
            if (summaryRefreshInFlightRef.current?.promise === refreshPromise) {
                summaryRefreshInFlightRef.current = null;
            }
        }
    }, [localize, onSummaryLoaded, operationId, requestId, serviceClient]);

    React.useEffect(() => {
        setSummary(null);
        void refreshSummary(true, true);
    }, [
        operationId,
        refreshSummary,
        requestId,
        sourceGapSummary?.dateSent,
        sourceGapSummary?.operationId,
        sourceGapSummary?.requestId
    ]);

    const markGapReportSent = React.useCallback(async (recipientEmail: string): Promise<void> => {
        setSummary(currentSummary => currentSummary
            ? {
                ...currentSummary,
                sentToEmail: recipientEmail,
                dateSent: currentSummary.dateSent ?? new Date().toISOString()
            }
            : currentSummary);

        await refreshSummary(false, true);
    }, [refreshSummary]);

    return {
        summary,
        isLoading,
        errorMessage,
        setErrorMessage,
        markGapReportSent
    };
};
